import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import PageTransition from './components/PageTransition';
import AdminLayout from './components/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './context/AuthContext';
import { PartnerAuthProvider } from './context/PartnerAuthContext';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
// import GalleryPage from './pages/GalleryPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import AdminPage from './pages/AdminPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminAccountPage from './pages/AdminAccountPage';
import AdminsPage from './pages/AdminsPage';
import PortfolioManagementPage from './pages/PortfolioManagementPage';
import BlogManagementPage from './pages/BlogManagementPage';
import EmailSenderPage from './pages/EmailSenderPage';
import BulkEmailPage from './pages/BulkEmailPage';

/* Partner Network — inside the admin console */
import PartnerProspectsPage from './pages/PartnerProspectsPage';
import PartnerProspectDetailPage from './pages/PartnerProspectDetailPage';
import PartnersPage from './pages/PartnersPage';
import PartnerDetailPage from './pages/PartnerDetailPage';
import ReferralsPage from './pages/ReferralsPage';
import ReferralDetailPage from './pages/ReferralDetailPage';
import CommissionsPage from './pages/CommissionsPage';

/* Partner portal — external partners */
import PartnerLayout from './components/partner-portal/PartnerLayout';
import PartnerProtectedRoute from './components/partner-portal/PartnerProtectedRoute';
import PartnerLogin from './components/partner-portal/PartnerLogin';
import PartnerRegister from './components/partner-portal/PartnerRegister';
import PartnerDashboard from './components/partner-portal/PartnerDashboard';
import PartnerReferrals from './components/partner-portal/PartnerReferrals';
import PartnerReferralDetail from './components/partner-portal/PartnerReferralDetail';
import SubmitReferral from './components/partner-portal/SubmitReferral';
import PartnerCommissions from './components/partner-portal/PartnerCommissions';
import PartnerNotifications from './components/partner-portal/PartnerNotifications';
import PartnerProfile from './components/partner-portal/PartnerProfile';

/** Content tools are for admins; marketers are bounced to their own board. */
const CONTENT_ROLES = ['admin', 'superadmin'] as const;

/** Public marketing site: header, footer and page transitions. */
const PublicRoutes: React.FC = () => (
  <Layout>
    <PageTransition>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        {/* <Route path="/gallery" element={<GalleryPage />} /> */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:id" element={<BlogDetailPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
    </PageTransition>
  </Layout>
);

function App() {
  return (
    <AuthProvider>
      <PartnerAuthProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/admin/login" element={<AdminLoginPage />} />

            {/* Admin console — its own shell, no marketing chrome. */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <ProtectedRoute roles={[...CONTENT_ROLES]}>
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
              <Route path="account" element={<AdminAccountPage />} />
              <Route
                path="portfolio"
                element={
                  <ProtectedRoute roles={[...CONTENT_ROLES]}>
                    <PortfolioManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="blog"
                element={
                  <ProtectedRoute roles={[...CONTENT_ROLES]}>
                    <BlogManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route path="email" element={<EmailSenderPage />} />
              <Route path="email/bulk" element={<BulkEmailPage />} />

              {/* Partner Network */}
              <Route
                path="partners"
                element={<Navigate to="/admin/partners/prospects" replace />}
              />
              <Route path="partners/prospects" element={<PartnerProspectsPage />} />
              <Route
                path="partners/prospects/:id"
                element={<PartnerProspectDetailPage />}
              />
              <Route path="partners/partners" element={<PartnersPage />} />
              <Route path="partners/partners/:id" element={<PartnerDetailPage />} />
              <Route path="partners/referrals" element={<ReferralsPage />} />
              <Route path="partners/referrals/:id" element={<ReferralDetailPage />} />
              <Route
                path="partners/commissions"
                element={
                  <ProtectedRoute roles={[...CONTENT_ROLES]}>
                    <CommissionsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="admins"
                element={
                  <ProtectedRoute superAdminOnly>
                    <AdminsPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Partner portal — external partners, separate session entirely. */}
            <Route path="/partner/login" element={<PartnerLogin />} />
            <Route path="/partner/register" element={<PartnerRegister />} />
            <Route
              path="/partner"
              element={
                <PartnerProtectedRoute>
                  <PartnerLayout />
                </PartnerProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/partner/dashboard" replace />} />
              <Route path="dashboard" element={<PartnerDashboard />} />
              <Route path="referrals" element={<PartnerReferrals />} />
              <Route path="referrals/new" element={<SubmitReferral />} />
              <Route path="referrals/:id" element={<PartnerReferralDetail />} />
              <Route path="commissions" element={<PartnerCommissions />} />
              <Route path="notifications" element={<PartnerNotifications />} />
              <Route path="profile" element={<PartnerProfile />} />
            </Route>

            <Route path="*" element={<PublicRoutes />} />
          </Routes>
        </Router>
      </PartnerAuthProvider>
    </AuthProvider>
  );
}

export default App;
